// src/client.ts
var ApiClientError = class extends Error {
  constructor(code, message, status, details) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
    this.name = "ApiClientError";
  }
};
var ApiClient = class {
  config;
  constructor(config) {
    this.config = config;
  }
  /**
   * Make an authenticated request
   */
  async request(method, path, options) {
    const base = this.config.baseUrl || (typeof window !== "undefined" ? window.location.origin : "");
    const url = new URL(path, base);
    if (options?.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== void 0) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    const token = await this.config.getAccessToken?.();
    const headers = {
      "Content-Type": "application/json",
      ...options?.headers
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const response = await fetch(url.toString(), {
      method,
      headers,
      body: options?.body ? JSON.stringify(options.body) : void 0
    });
    if (response.status === 401) {
      this.config.onUnauthorized?.();
      throw new ApiClientError("UNAUTHORIZED", "Unauthorized", 401);
    }
    const contentType = response.headers.get("content-type") ?? "";
    if (!response.ok) {
      if (contentType.includes("application/json")) {
        const error = await response.json();
        if ("error" in error) {
          throw new ApiClientError(
            error.error.code,
            error.error.message,
            response.status,
            error.error.details
          );
        }
        throw new ApiClientError(error.code ?? "HTTP_ERROR", error.message, response.status);
      }
      throw new ApiClientError("HTTP_ERROR", response.statusText || "Request failed", response.status);
    }
    if (options?.responseType === "blob") {
      return await response.blob();
    }
    if (options?.responseType === "text") {
      return await response.text();
    }
    if (!contentType || !contentType.includes("application/json")) {
      return void 0;
    }
    const payload = await response.json();
    if (payload && typeof payload === "object" && "success" in payload) {
      if (!payload.success) {
        const error = payload;
        throw new ApiClientError(
          error.error.code,
          error.error.message,
          response.status,
          error.error.details
        );
      }
      return payload.data;
    }
    return payload;
  }
  /**
   * GET request
   */
  async get(path, params, options) {
    return this.request("GET", path, { params, ...options });
  }
  /**
   * POST request
   */
  async post(path, body, options) {
    return this.request("POST", path, { ...options, body });
  }
  /**
   * PUT request
   */
  async put(path, body, options) {
    return this.request("PUT", path, { ...options, body });
  }
  /**
   * PATCH request
   */
  async patch(path, body, options) {
    return this.request("PATCH", path, { ...options, body });
  }
  /**
   * DELETE request
   */
  async delete(path, options) {
    return this.request("DELETE", path, options);
  }
};
var apiClient = null;
function initializeApiClient(config) {
  apiClient = new ApiClient(config);
  return apiClient;
}
function getApiClient() {
  if (!apiClient) {
    throw new Error("API client not initialized. Call initializeApiClient first.");
  }
  return apiClient;
}

// src/endpoints.ts
var API_ENDPOINTS = {
  // Auth
  AUTH: {
    ME: "/api/auth/me",
    LOGOUT: "/api/auth/logout"
  },
  // Tenants
  TENANTS: {
    LIST: "/api/tenants",
    GET: (id) => `/api/tenants/${id}`,
    CREATE: "/api/tenants",
    UPDATE: (id) => `/api/tenants/${id}`,
    DELETE: (id) => `/api/tenants/${id}`
  },
  // Users
  USERS: {
    LIST: "/api/users",
    GET: (id) => `/api/users/${id}`,
    CREATE: "/api/users",
    UPDATE: (id) => `/api/users/${id}`,
    DELETE: (id) => `/api/users/${id}`
  },
  // Contacts
  CONTACTS: {
    LIST: "/api/contacts",
    GET: (id) => `/api/contacts/${id}`,
    CREATE: "/api/contacts",
    UPDATE: (id) => `/api/contacts/${id}`,
    DELETE: (id) => `/api/contacts/${id}`,
    IMPORT: "/api/contacts/import",
    EXPORT: "/api/contacts/export",
    BULK_DELETE: "/api/contacts/bulk/delete",
    BULK_TAGS: "/api/contacts/bulk/tags"
  },
  // Tags
  TAGS: {
    LIST: "/api/tags",
    GET: (id) => `/api/tags/${id}`,
    CREATE: "/api/tags",
    UPDATE: (id) => `/api/tags/${id}`,
    DELETE: (id) => `/api/tags/${id}`
  },
  // Contact Groups (Pipelines)
  GROUPS: {
    LIST: "/api/groups",
    GET: (id) => `/api/groups/${id}`,
    CREATE: "/api/groups",
    UPDATE: (id) => `/api/groups/${id}`,
    DELETE: (id) => `/api/groups/${id}`,
    REORDER: "/api/groups/reorder",
    // Stages
    STAGES: (groupId) => `/api/groups/${groupId}/stages`,
    STAGE: (groupId, stageId) => `/api/groups/${groupId}/stages/${stageId}`,
    STAGES_REORDER: (groupId) => `/api/groups/${groupId}/stages/reorder`,
    // Contacts in group
    CONTACTS: (groupId) => `/api/groups/${groupId}/contacts`,
    MOVE_CONTACT: (groupId, contactId) => `/api/groups/${groupId}/contacts/${contactId}/move`,
    REMOVE_CONTACT: (groupId, contactId) => `/api/groups/${groupId}/contacts/${contactId}`,
    CONTACTS_REORDER: (groupId) => `/api/groups/${groupId}/contacts/reorder`,
    // Contact's groups
    CONTACT_GROUPS: (contactId) => `/api/groups/contact/${contactId}`
  },
  // Send flow resources
  SEND: {
    RESOURCES: "/api/send/resources",
    SCHEDULE_PREVIEW: "/api/send/schedule-preview"
  },
  // Forms
  FORMS: {
    LIST: "/api/forms",
    GET: (id) => `/api/forms/${id}`,
    CREATE: "/api/forms",
    UPDATE: (id) => `/api/forms/${id}`,
    DELETE: (id) => `/api/forms/${id}`,
    STATS: "/api/forms/stats",
    LIMIT_CHECK: "/api/forms/limit-check",
    DASHBOARD: (id) => `/api/forms/${id}/dashboard`,
    DUPLICATE: (id) => `/api/forms/${id}/duplicate`,
    PUBLISH: (id) => `/api/forms/${id}/publish`,
    UNPUBLISH: (id) => `/api/forms/${id}/unpublish`,
    SUBMISSIONS: (id) => `/api/forms/${id}/submissions`,
    LINK_SUBMISSION: (submissionId) => `/api/forms/submissions/${submissionId}/link`,
    DELETE_SUBMISSION: (submissionId) => `/api/forms/submissions/${submissionId}`,
    EXPORT: (id) => `/api/forms/${id}/export`,
    QR_CODE: (id) => `/api/forms/${id}/qr-code`
  },
  // Documents
  DOCUMENTS: {
    LIST: "/api/documents",
    GET: (id) => `/api/documents/${id}`,
    CREATE: "/api/documents",
    UPDATE: (id) => `/api/documents/${id}`,
    DELETE: (id) => `/api/documents/${id}`
  },
  // Signatures
  SIGNATURES: {
    LIST: "/api/signatures",
    GET: (id) => `/api/signatures/${id}`,
    REQUEST: "/api/signatures/request",
    REMIND: (id) => `/api/signatures/${id}/remind`
  },
  // Campaigns
  CAMPAIGNS: {
    LIST: "/api/campaigns",
    GET: (id) => `/api/campaigns/${id}`,
    CREATE: "/api/campaigns",
    UPDATE: (id) => `/api/campaigns/${id}`,
    DELETE: (id) => `/api/campaigns/${id}`,
    SEND: (id) => `/api/campaigns/${id}/send`,
    PAUSE: (id) => `/api/campaigns/${id}/pause`,
    RESUME: (id) => `/api/campaigns/${id}/resume`,
    CANCEL: (id) => `/api/campaigns/${id}/cancel`,
    DUPLICATE: (id) => `/api/campaigns/${id}/duplicate`,
    RECIPIENTS: (id) => `/api/campaigns/${id}/recipients`,
    // Activity Detail endpoints
    TIMELINE: (id) => `/api/campaigns/${id}/timeline`,
    REMIND: (id) => `/api/campaigns/${id}/remind`,
    DOWNLOAD: (id) => `/api/campaigns/${id}/download`,
    // Wizard support endpoints
    RESOLVE_RECIPIENTS: "/api/campaigns/resolve-recipients",
    ESTIMATE_COST: "/api/campaigns/estimate-cost",
    COMPLIANCE_CHECK: "/api/campaigns/compliance-check"
  },
  // Conversations
  CONVERSATIONS: {
    LIST: "/api/conversations",
    GET: (id) => `/api/conversations/${id}`,
    UPDATE: (id) => `/api/conversations/${id}`,
    MESSAGES: (id) => `/api/conversations/${id}/messages`,
    REPLY: (id) => `/api/conversations/${id}/messages`,
    RESOLVE: (id) => `/api/conversations/${id}/resolve`,
    REOPEN: (id) => `/api/conversations/${id}/reopen`,
    UNREAD_COUNT: "/api/conversations/unread-count"
  },
  // Tenant Support (Phase 1 + 2)
  SUPPORT: {
    REQUESTS: "/api/support/requests",
    REQUEST: (id) => `/api/support/requests/${id}`,
    MESSAGES: (id) => `/api/support/requests/${id}/messages`,
    UPLOAD: (id) => `/api/support/requests/${id}/upload`
  },
  // Voice
  VOICE: {
    LIST: "/api/voice/calls",
    GET: (id) => `/api/voice/calls/${id}`,
    INITIATE: "/api/voice/calls"
  },
  // Analytics
  ANALYTICS: {
    OVERVIEW: "/api/analytics/overview",
    SENDS: "/api/analytics/sends",
    DELIVERY: "/api/analytics/delivery",
    DOCUMENTS: "/api/analytics/documents",
    FORMS: "/api/analytics/forms",
    DISTRIBUTION: "/api/analytics/distribution",
    PERFORMANCE: "/api/analytics/performance",
    EXPORT: "/api/analytics/export"
  },
  // Webhooks
  WEBHOOKS: {
    LIST: "/api/webhooks",
    GET: (id) => `/api/webhooks/${id}`,
    CREATE: "/api/webhooks",
    UPDATE: (id) => `/api/webhooks/${id}`,
    DELETE: (id) => `/api/webhooks/${id}`,
    TEST: (id) => `/api/webhooks/${id}/test`
  },
  // AI
  AI: {
    GENERATE_MESSAGE: "/api/ai/generate-message",
    GENERATE_VOICE_SCRIPT: "/api/ai/generate-voice-script",
    GENERATE_FORM: "/api/ai/generate-form",
    EDIT_FORM: "/api/ai/edit-form",
    GENERATE_NDF: "/api/ai/generate-ndf",
    EDIT_NDF: "/api/ai/edit-ndf",
    GENERATE_TEMPLATE: "/api/ai/generate-template",
    GENERATE_MULTI_CHANNEL: "/api/ai/generate-multi-channel",
    REGENERATE_CHANNEL: "/api/ai/regenerate-channel",
    ANALYZE_SEND_INTENT: "/api/ai/analyze-send-intent",
    ANALYZE_CHANNEL: "/api/ai/analyze-channel",
    SUGGEST_REPLY: "/api/ai/suggest-reply"
  },
  // Templates
  TEMPLATES: {
    LIST: "/api/templates",
    GET: (id) => `/api/templates/${id}`,
    CREATE: "/api/templates",
    UPDATE: (id) => `/api/templates/${id}`,
    DELETE: (id) => `/api/templates/${id}`
  },
  // Voice Scripts
  VOICE_SCRIPTS: {
    LIST: "/api/voice-scripts",
    GET: (id) => `/api/voice-scripts/${id}`,
    CREATE: "/api/voice-scripts",
    UPDATE: (id) => `/api/voice-scripts/${id}`,
    DELETE: (id) => `/api/voice-scripts/${id}`
  },
  // Unified Inbox
  UNIFIED_INBOX: {
    LIST: "/api/inbox",
    GET: (id) => `/api/inbox/${id}`,
    COUNTS: "/api/inbox/counts",
    BULK_DELETE: "/api/inbox/bulk/delete",
    BULK_ARCHIVE: "/api/inbox/bulk/archive",
    BULK_MARK_READ: "/api/inbox/bulk/mark-read",
    BULK_MARK_UNREAD: "/api/inbox/bulk/mark-unread",
    BULK_RESEND: "/api/inbox/bulk/resend",
    BULK_EXPORT: "/api/inbox/bulk/export"
  },
  // Policy Routing Engine (tenant-facing)
  PRE: {
    EFFECTIVE_RULES: "/api/pre/rules",
    CHECK_DEFINITIONS: "/api/pre/check-definitions",
    OVERRIDES: {
      LIST: "/api/pre/overrides",
      CREATE: "/api/pre/overrides",
      UPDATE: (id) => `/api/pre/overrides/${id}`,
      DELETE: (id) => `/api/pre/overrides/${id}`
    },
    DECISIONS: {
      LIST: "/api/pre/decisions",
      GET: (id) => `/api/pre/decisions/${id}`
    },
    CLASSIFY: "/api/pre/classify",
    EVALUATE: "/api/pre/evaluate",
    ELIGIBILITY: (contactId) => `/api/pre/eligibility/${contactId}`,
    AI_CLASSIFICATIONS: {
      LIST: "/api/pre/ai-classifications"
    },
    CONSENT_RECORDS: (contactId) => `/api/pre/consent-records/${contactId}`
  },
  // Changes (Audit Log / Notifications)
  CHANGES: {
    LIST: "/api/changes",
    UNREAD_COUNT: "/api/changes/unread-count",
    ENTITY: (entityType, entityId) => `/api/changes/entity/${entityType}/${entityId}`,
    MARK_READ: (id) => `/api/changes/${id}/read`,
    BULK_MARK_READ: "/api/changes/mark-read"
  },
  // Super Admin Portal
  SUPER_ADMIN: {
    AUTH: {
      LOGIN: "/api/super-admin/auth/login",
      LOGOUT: "/api/super-admin/auth/logout",
      REFRESH: "/api/super-admin/auth/refresh",
      ME: "/api/super-admin/auth/me"
    },
    // Phase 2: Accounts & Users
    ACCOUNTS: {
      LIST: "/api/super-admin/accounts",
      GET: (id) => `/api/super-admin/accounts/${id}`,
      CREATE: "/api/super-admin/accounts",
      UPDATE: (id) => `/api/super-admin/accounts/${id}`,
      DELETE: (id) => `/api/super-admin/accounts/${id}`,
      // Lifecycle actions
      SUSPEND: (id) => `/api/super-admin/accounts/${id}/suspend`,
      REACTIVATE: (id) => `/api/super-admin/accounts/${id}/reactivate`,
      CANCEL: (id) => `/api/super-admin/accounts/${id}/cancel`,
      EXTEND_TRIAL: (id) => `/api/super-admin/accounts/${id}/extend-trial`,
      // Nested resources
      USERS: (id) => `/api/super-admin/accounts/${id}/users`,
      USAGE: (id) => `/api/super-admin/accounts/${id}/usage`,
      AUDIT: (id) => `/api/super-admin/accounts/${id}/audit`,
      PHONE_NUMBERS: (id) => `/api/super-admin/accounts/${id}/phone-numbers`,
      // Telnyx (dedicated number provisioning)
      TELNYX: {
        STATUS: (id) => `/api/super-admin/accounts/${id}/telnyx/status`,
        CREATE_PROFILE: (id) => `/api/super-admin/accounts/${id}/telnyx/profile`,
        CREATE_BRAND: (id) => `/api/super-admin/accounts/${id}/telnyx/brand`,
        REFRESH_BRAND: (id) => `/api/super-admin/accounts/${id}/telnyx/brand/refresh`,
        CREATE_CAMPAIGN: (id) => `/api/super-admin/accounts/${id}/telnyx/campaign`,
        REFRESH_CAMPAIGN: (id) => `/api/super-admin/accounts/${id}/telnyx/campaign/refresh`,
        SUBMIT_TFV: (id) => `/api/super-admin/accounts/${id}/telnyx/tfv`,
        REFRESH_TFV: (id) => `/api/super-admin/accounts/${id}/telnyx/tfv/refresh`,
        SEARCH_NUMBERS: (id) => `/api/super-admin/accounts/${id}/telnyx/numbers/search`,
        PROVISION_NUMBER: (id) => `/api/super-admin/accounts/${id}/telnyx/numbers/provision`,
        UPDATE_NUMBER: (accountId, phoneNumberId) => `/api/super-admin/accounts/${accountId}/telnyx/numbers/${phoneNumberId}`,
        RELEASE_NUMBER: (accountId, phoneNumberId) => `/api/super-admin/accounts/${accountId}/telnyx/numbers/${phoneNumberId}`
      },
      // Number Inventory (multi-tenant phone-number assignments)
      INVENTORY: {
        LIST_TELNYX: "/api/super-admin/inventory/telnyx",
        IMPORT: "/api/super-admin/inventory/import",
        DETAIL: (phoneNumberId) => `/api/super-admin/inventory/${phoneNumberId}`,
        ASSIGNMENTS: (phoneNumberId) => `/api/super-admin/inventory/${phoneNumberId}/assignments`,
        ASSIGNMENT: (phoneNumberId, tenantId) => `/api/super-admin/inventory/${phoneNumberId}/assignments/${tenantId}`
      },
      // Impersonation
      IMPERSONATE: (id) => `/api/super-admin/accounts/${id}/impersonate`,
      END_IMPERSONATION: "/api/super-admin/accounts/end-impersonation",
      // Notes
      NOTES: (id) => `/api/super-admin/accounts/${id}/notes`,
      NOTE: (accountId, noteId) => `/api/super-admin/accounts/${accountId}/notes/${noteId}`,
      // Analytics
      GROWTH_TREND: "/api/super-admin/accounts/growth-trend"
    },
    // Phase 3: Support & Communications
    SUPPORT: {
      // Support Requests
      REQUESTS: "/api/super-admin/support/requests",
      REQUEST: (id) => `/api/super-admin/support/requests/${id}`,
      UPDATE_REQUEST: (id) => `/api/super-admin/support/requests/${id}`,
      ASSIGN: (id) => `/api/super-admin/support/requests/${id}/assign`,
      RESOLVE: (id) => `/api/super-admin/support/requests/${id}/resolve`,
      REOPEN: (id) => `/api/super-admin/support/requests/${id}/reopen`,
      CLOSE: (id) => `/api/super-admin/support/requests/${id}/close`,
      MESSAGES: (id) => `/api/super-admin/support/requests/${id}/messages`,
      UPLOAD: (id) => `/api/super-admin/support/requests/${id}/upload`,
      // Queue
      QUEUE_SUMMARY: "/api/super-admin/support/queue/summary",
      // AI Flags
      AI_FLAGS: "/api/super-admin/support/ai-flags",
      AI_FLAG: (id) => `/api/super-admin/support/ai-flags/${id}`,
      AI_FLAG_REVIEW: (id) => `/api/super-admin/support/ai-flags/${id}/review`
    },
    // Phase 4: Plans & Billing
    PLANS: {
      LIST: "/api/super-admin/plans",
      CREATE: "/api/super-admin/plans",
      PLAN: (id) => `/api/super-admin/plans/${id}`,
      UPDATE: (id) => `/api/super-admin/plans/${id}`,
      DELETE: (id) => `/api/super-admin/plans/${id}`,
      SUBSCRIBERS: (id) => `/api/super-admin/plans/${id}/subscribers`,
      RESYNC_HELCIM: (id) => `/api/super-admin/plans/${id}/resync-helcim`,
      RESYNC_HELCIM_ALL: "/api/super-admin/plans/resync-helcim-all"
    },
    BILLING: {
      DASHBOARD: "/api/super-admin/billing/dashboard",
      METRICS: "/api/super-admin/billing/metrics",
      REVENUE_TREND: "/api/super-admin/billing/revenue-trend",
      INVOICES: "/api/super-admin/billing/invoices",
      INVOICE: (id) => `/api/super-admin/billing/invoices/${id}`,
      INVOICE_REFUND: (id) => `/api/super-admin/billing/invoices/${id}/refund`,
      INVOICE_CANCEL: (id) => `/api/super-admin/billing/invoices/${id}/cancel`,
      PAST_DUE: "/api/super-admin/billing/past-due",
      PAYMENTS: "/api/super-admin/billing/payments",
      PLAN_DISTRIBUTION: "/api/super-admin/billing/plan-distribution",
      DISPUTES: "/api/super-admin/billing/disputes",
      USAGE_OVERAGES: "/api/super-admin/billing/usage-overages",
      FREE_PLAN_ACCOUNTS: "/api/super-admin/billing/free-plan-accounts",
      // Add-ons Catalog
      ADD_ONS: "/api/super-admin/billing/add-ons",
      ADD_ONS_ANALYTICS: "/api/super-admin/add-ons/analytics",
      ADD_ON: (id) => `/api/super-admin/billing/add-ons/${id}`,
      ADD_ON_PLAN_ASSIGNMENTS: (id) => `/api/super-admin/billing/add-ons/${id}/plan-assignments`,
      // Plan-Add-on Assignment
      PLAN_ADD_ONS: (planId) => `/api/super-admin/plans/${planId}/add-ons`,
      PLAN_ADD_ON: (planId, addOnId) => `/api/super-admin/plans/${planId}/add-ons/${addOnId}`
    },
    DISCOUNTS: {
      LIST: "/api/super-admin/discounts",
      CREATE: "/api/super-admin/discounts",
      GET: (id) => `/api/super-admin/discounts/${id}`,
      UPDATE: (id) => `/api/super-admin/discounts/${id}`,
      DELETE: (id) => `/api/super-admin/discounts/${id}`,
      VALIDATE_CODE: "/api/super-admin/discounts/validate-code",
      ASSIGN_TO_TENANT: (tenantId) => `/api/super-admin/accounts/${tenantId}/discounts`,
      TENANT_DISCOUNTS: (tenantId) => `/api/super-admin/accounts/${tenantId}/discounts`,
      REMOVE_FROM_TENANT: (tenantId, assignmentId) => `/api/super-admin/accounts/${tenantId}/discounts/${assignmentId}`
    },
    // Phase 5: Templates
    TEMPLATES: {
      LIST: "/api/super-admin/templates",
      CREATE: "/api/super-admin/templates",
      TEMPLATE: (id) => `/api/super-admin/templates/${id}`,
      UPDATE: (id) => `/api/super-admin/templates/${id}`,
      DELETE: (id) => `/api/super-admin/templates/${id}`,
      VISIBILITY: (id) => `/api/super-admin/templates/${id}/visibility`,
      DUPLICATE: (id) => `/api/super-admin/templates/${id}/duplicate`
    },
    // Phase 5: Platform Campaigns
    CAMPAIGNS: {
      LIST: "/api/super-admin/campaigns",
      CREATE: "/api/super-admin/campaigns",
      CAMPAIGN: (id) => `/api/super-admin/campaigns/${id}`,
      UPDATE: (id) => `/api/super-admin/campaigns/${id}`,
      DELETE: (id) => `/api/super-admin/campaigns/${id}`,
      ACTIVATE: (id) => `/api/super-admin/campaigns/${id}/activate`,
      PAUSE: (id) => `/api/super-admin/campaigns/${id}/pause`,
      STATS: (id) => `/api/super-admin/campaigns/${id}/stats`,
      ENROLLMENTS: "/api/super-admin/campaigns/enrollments"
    },
    // Phase 5: Account Tags
    TAGS: {
      LIST: "/api/super-admin/tags",
      CREATE: "/api/super-admin/tags",
      TAG: (id) => `/api/super-admin/tags/${id}`,
      DELETE: (id) => `/api/super-admin/tags/${id}`
    },
    // Phase 6: Operations
    OPERATIONS: {
      // Health monitoring
      HEALTH: "/api/super-admin/operations/health",
      HEALTH_HISTORY: "/api/super-admin/operations/health/history",
      // SSE stream for real-time refresh hints
      STREAM: "/api/super-admin/operations/stream",
      // Maintenance mode
      MAINTENANCE: "/api/super-admin/operations/maintenance",
      // Cache management
      CACHE_INVALIDATE: "/api/super-admin/operations/cache/invalidate",
      // Queue management
      QUEUES_STATS: "/api/super-admin/operations/queues/stats",
      QUEUE_PAUSE: (name) => `/api/super-admin/operations/queues/${name}/pause`,
      QUEUE_RESUME: (name) => `/api/super-admin/operations/queues/${name}/resume`,
      // Provider status
      PROVIDERS_STATUS: "/api/super-admin/operations/providers/status"
    },
    // Phase 6: Audit
    AUDIT: {
      LIST: "/api/super-admin/audit",
      GET: (id) => `/api/super-admin/audit/${id}`,
      EXPORT: "/api/super-admin/audit/export",
      STATS: "/api/super-admin/audit/stats"
    },
    // Phase 7: Admin Users
    USERS: {
      LIST: "/api/super-admin/users",
      CREATE: "/api/super-admin/users",
      GET: (id) => `/api/super-admin/users/${id}`,
      UPDATE: (id) => `/api/super-admin/users/${id}`,
      DELETE: (id) => `/api/super-admin/users/${id}`,
      RESET_PASSWORD: (id) => `/api/super-admin/users/${id}/reset-password`
    },
    // Phase 7: Feature Flags
    FEATURE_FLAGS: {
      LIST: "/api/super-admin/feature-flags",
      CREATE: "/api/super-admin/feature-flags",
      GET: (id) => `/api/super-admin/feature-flags/${id}`,
      UPDATE: (id) => `/api/super-admin/feature-flags/${id}`,
      TOGGLE: (id) => `/api/super-admin/feature-flags/${id}/toggle`,
      DELETE: (id) => `/api/super-admin/feature-flags/${id}`
    },
    // SMS Delivery Templates
    SMS_TEMPLATES: {
      LIST: "/api/super-admin/sms-templates",
      CREATE: "/api/super-admin/sms-templates",
      GET: (id) => `/api/super-admin/sms-templates/${id}`,
      UPDATE: (id) => `/api/super-admin/sms-templates/${id}`,
      DELETE: (id) => `/api/super-admin/sms-templates/${id}`,
      SET_DEFAULT: (id) => `/api/super-admin/sms-templates/${id}/set-default`
    },
    // Shared SMS Sender Pool (outbound from-number distribution)
    SMS_SENDERS: {
      LIST: "/api/super-admin/sms-senders",
      CREATE: "/api/super-admin/sms-senders",
      UPDATE: (id) => `/api/super-admin/sms-senders/${id}`,
      DELETE: (id) => `/api/super-admin/sms-senders/${id}`
    },
    // Email Templates
    EMAIL_TEMPLATES: {
      LIST: "/api/super-admin/email-templates",
      GET: (key) => `/api/super-admin/email-templates/${key}`,
      UPDATE: (key) => `/api/super-admin/email-templates/${key}`,
      RESET: (key) => `/api/super-admin/email-templates/${key}/reset`,
      PREVIEW: (key) => `/api/super-admin/email-templates/${key}/preview`,
      TEST_SEND: (key) => `/api/super-admin/email-templates/${key}/test-send`
    },
    // Phase 7: Settings
    SETTINGS: {
      EMAIL: "/api/super-admin/settings/email",
      SMS: "/api/super-admin/settings/sms",
      COMPLIANCE: "/api/super-admin/settings/compliance"
    },
    // Internal DNC — platform-wide Do-Not-Contact list for outbound SMS.
    INTERNAL_DNC: {
      GET: "/api/super-admin/internal-dnc",
      UPDATE: "/api/super-admin/internal-dnc",
      RECENT_SENDS: "/api/super-admin/internal-dnc/recent-sends"
    },
    // Cross-tenant Contacts (super admin only)
    CONTACTS: {
      SEARCH: "/api/super-admin/contacts/search"
    },
    // Usage Analytics
    USAGE: {
      DASHBOARD: "/api/super-admin/usage/dashboard"
    },
    // Policy Routing Engine
    PRE: {
      JURISDICTIONS: {
        LIST: "/api/super-admin/pre/jurisdictions",
        CREATE: "/api/super-admin/pre/jurisdictions",
        GET: (id) => `/api/super-admin/pre/jurisdictions/${id}`,
        UPDATE: (id) => `/api/super-admin/pre/jurisdictions/${id}`,
        DELETE: (id) => `/api/super-admin/pre/jurisdictions/${id}`
      },
      RULES: {
        LIST: "/api/super-admin/pre/rules",
        CREATE: "/api/super-admin/pre/rules",
        GET: (id) => `/api/super-admin/pre/rules/${id}`,
        UPDATE: (id) => `/api/super-admin/pre/rules/${id}`,
        DELETE: (id) => `/api/super-admin/pre/rules/${id}`,
        ACTIVATE: (id) => `/api/super-admin/pre/rules/${id}/activate`,
        DEACTIVATE: (id) => `/api/super-admin/pre/rules/${id}/deactivate`,
        VERSIONS: (id) => `/api/super-admin/pre/rules/${id}/versions`,
        IMPORT: "/api/super-admin/pre/rules/import",
        EXPORT: "/api/super-admin/pre/rules/export"
      },
      BUNDLE: {
        EXPORT: "/api/super-admin/pre/bundle/export",
        VALIDATE: "/api/super-admin/pre/bundle/validate",
        DIFF: "/api/super-admin/pre/bundle/diff",
        APPLY: "/api/super-admin/pre/bundle/apply",
        PROMOTE: "/api/super-admin/pre/bundle/promote",
        GOLDEN_LIST: "/api/super-admin/pre/bundle/golden",
        GOLDEN_GET: (id) => `/api/super-admin/pre/bundle/golden/${id}`,
        GOLDEN_PAYLOAD: (id) => `/api/super-admin/pre/bundle/golden/${id}/payload`,
        GOLDEN_APPROVE: (id) => `/api/super-admin/pre/bundle/golden/${id}/approve`,
        DRIFT: "/api/super-admin/pre/bundle/drift",
        DRIFT_CHECK: "/api/super-admin/pre/bundle/drift/check"
      },
      CALLING_HOURS: {
        LIST: "/api/super-admin/pre/calling-hours",
        CREATE: "/api/super-admin/pre/calling-hours",
        GET: (id) => `/api/super-admin/pre/calling-hours/${id}`,
        UPDATE: (id) => `/api/super-admin/pre/calling-hours/${id}`,
        DELETE: (id) => `/api/super-admin/pre/calling-hours/${id}`
      },
      DNC: {
        LIST: "/api/super-admin/pre/dnc-registries",
        CREATE: "/api/super-admin/pre/dnc-registries",
        GET: (id) => `/api/super-admin/pre/dnc-registries/${id}`,
        UPDATE: (id) => `/api/super-admin/pre/dnc-registries/${id}`,
        DELETE: (id) => `/api/super-admin/pre/dnc-registries/${id}`
      },
      FREQUENCY_CAPS: {
        LIST: "/api/super-admin/pre/frequency-caps",
        CREATE: "/api/super-admin/pre/frequency-caps",
        GET: (id) => `/api/super-admin/pre/frequency-caps/${id}`,
        UPDATE: (id) => `/api/super-admin/pre/frequency-caps/${id}`,
        DELETE: (id) => `/api/super-admin/pre/frequency-caps/${id}`
      },
      CHECK_DEFINITIONS: {
        LIST: "/api/super-admin/pre/check-definitions",
        CREATE: "/api/super-admin/pre/check-definitions",
        GET: (id) => `/api/super-admin/pre/check-definitions/${id}`,
        UPDATE: (id) => `/api/super-admin/pre/check-definitions/${id}`,
        DELETE: (id) => `/api/super-admin/pre/check-definitions/${id}`
      },
      OVERRIDES: {
        LIST: "/api/super-admin/pre/overrides",
        CREATE: "/api/super-admin/pre/overrides",
        GET: (id) => `/api/super-admin/pre/overrides/${id}`,
        UPDATE: (id) => `/api/super-admin/pre/overrides/${id}`,
        DELETE: (id) => `/api/super-admin/pre/overrides/${id}`
      },
      DECISIONS: {
        LIST: "/api/super-admin/pre/decisions",
        GET: (id) => `/api/super-admin/pre/decisions/${id}`,
        EXPORT: "/api/super-admin/pre/decisions/export",
        STATS: "/api/super-admin/pre/decisions/stats"
      },
      AI: {
        LIST: "/api/super-admin/pre/ai-classifications",
        GET: (id) => `/api/super-admin/pre/ai-classifications/${id}`,
        OVERRIDE: (id) => `/api/super-admin/pre/ai-classifications/${id}/override`,
        STATS: "/api/super-admin/pre/ai-classifications/stats"
      }
    }
  },
  // Team Management — Roles (per-tenant custom roles)
  ROLES: {
    LIST: (tenantId) => `/api/tenants/${tenantId}/roles`,
    CREATE: (tenantId) => `/api/tenants/${tenantId}/roles`,
    GET: (tenantId, roleId) => `/api/tenants/${tenantId}/roles/${roleId}`,
    UPDATE: (tenantId, roleId) => `/api/tenants/${tenantId}/roles/${roleId}`,
    DELETE: (tenantId, roleId) => `/api/tenants/${tenantId}/roles/${roleId}`
  },
  // Team Management — Members & Invitations
  TEAM: {
    MEMBERS: (tenantId) => `/api/tenants/${tenantId}/team/members`,
    INVITATIONS: (tenantId) => `/api/tenants/${tenantId}/team/invitations`,
    RESEND: (tenantId, invId) => `/api/tenants/${tenantId}/team/invitations/${invId}/resend`,
    REVOKE: (tenantId, invId) => `/api/tenants/${tenantId}/team/invitations/${invId}`,
    CHANGE_ROLE: (tenantId, userId) => `/api/tenants/${tenantId}/team/members/${userId}/role`,
    CHANGE_STATUS: (tenantId, userId) => `/api/tenants/${tenantId}/team/members/${userId}/status`,
    REMOVE: (tenantId, userId) => `/api/tenants/${tenantId}/team/members/${userId}`
  },
  // Public — Invitation flow
  // VALIDATE / ACCEPT are unauthenticated; ACCEPT_EXISTING requires a JWT.
  INVITATIONS: {
    VALIDATE: "/public/invitations/validate",
    ACCEPT: "/public/invitations/accept",
    ACCEPT_EXISTING: "/public/invitations/accept-existing"
  },
  // Workflow Automation
  WORKFLOWS: {
    LIST: "/api/workflows",
    CREATE: "/api/workflows",
    GET: (id) => `/api/workflows/${id}`,
    UPDATE: (id) => `/api/workflows/${id}`,
    DELETE: (id) => `/api/workflows/${id}`,
    ACTIVATE: (id) => `/api/workflows/${id}/activate`,
    PAUSE: (id) => `/api/workflows/${id}/pause`,
    DUPLICATE: (id) => `/api/workflows/${id}/duplicate`,
    STATS: (id) => `/api/workflows/${id}/stats`,
    ENROLLMENTS: (id) => `/api/workflows/${id}/enrollments`,
    ENROLL: (id) => `/api/workflows/${id}/enroll`
  },
  // Account Billing (Helcim)
  BILLING: {
    SUBSCRIPTION: "/api/billing/subscription",
    UPGRADE: "/api/billing/subscription/upgrade",
    DOWNGRADE: "/api/billing/subscription/downgrade",
    CANCEL: "/api/billing/subscription/cancel",
    USAGE: "/api/billing/usage",
    USAGE_TYPE: (type) => `/api/billing/usage/${type}`,
    INVOICES: "/api/billing/invoices",
    INVOICE: (id) => `/api/billing/invoices/${id}`,
    PAYMENT_METHODS: "/api/billing/payment-methods",
    PAYMENT_METHOD: (id) => `/api/billing/payment-methods/${id}`,
    ADD_ONS: "/api/billing/add-ons",
    PLANS: "/api/billing/plans",
    VALIDATE_DISCOUNT: "/api/billing/discounts/validate",
    SIGNUP_FINALIZE: "/api/billing/signup-finalize"
  },
  // Setup Wizard (OOBE)
  SETUP: {
    PROGRESS: "/api/setup/progress",
    FETCH_BRAND_ASSETS: "/api/setup/fetch-brand-assets"
  }
};

export { API_ENDPOINTS, ApiClient, ApiClientError, getApiClient, initializeApiClient };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map